import {useState} from 'react';
import axiosWidgetInstance, {withApiKey, widgetEndpoints} from 'packages/common/utils/axiosWidget';
import {widgetLogger} from 'packages/common/utils/widgetLogger';

/**
 * @file Hook d'inscription en liste d'attente de réservation pour le widget embarqué (auth par clé API).
 * @module packages.common.hooks.api.widgets.restaurants.Waitlist
 *
 * Câblé sur `axiosWidget` et l'endpoint `externalApi.restaurant.reservation.waitlist`,
 * la clé API étant injectée via `withApiKey`. Le hook public équivalent vit dans
 * `api/public/restaurants/Waitlist`.
 */

/**
 * Crée impérativement une inscription en liste d'attente via l'API externe.
 *
 * Appelle `POST reservation.waitlist(companyId)` et résout `data` (réponse JSON
 * brute du backend).
 *
 * @return {Object} `{createWaitlistEntry, loading, error}`
 * @return {Function} return.createWaitlistEntry - `(companyId, data, apiKey) => Promise<Object>`
 * @return {boolean} return.loading - Chargement en cours
 * @return {Error|null} return.error - Erreur éventuelle
 */
export const useExternalCreateWaitlistEntry = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createWaitlistEntry = async (companyId, data, apiKey) => {
    setLoading(true);
    setError(null);

    try {
      // Transmettre le parcours complet (allergies, demandes, brouillon...) comme
      // la création de réservation : le backend filtre les champs autorisés.
      const waitlistData = {
        ...data,
        numberOfGuests: parseInt(data.numberOfGuests, 10) || 1,
      };

      const response = await axiosWidgetInstance.post(
          widgetEndpoints.externalApi.restaurant.reservation.waitlist(companyId),
          waitlistData,
          withApiKey(apiKey),
      );

      return response.data;
    } catch (apiError) {
      widgetLogger.error('Error creating waitlist entry:', apiError.message);
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  };

  return {createWaitlistEntry, loading, error};
};
